/**
 * Loading and saving an event's campaign hierarchy against the service.
 *
 * The service keeps the hierarchy as one resource per node (AWSECEventHub campaign-hierarchy
 * model): every node points at its parent, a tree is anchored at an event's own node
 * (`LIFE_CYCLE_EVENT`), and the SFDC campaign ids beneath it are `EXTERNAL_CAMPAIGN` children.
 * There is no "save this tree" call, so a save is a diff turned into calls.
 *
 *   load   ListCampaignRoots?eventId= finds the event's node (root or child; an event backs
 *          at most one node), GetCampaignTree reads the subtree beneath it. No node: no tree.
 *
 *   save   top-down over the edited tree —
 *            a node with a nodeId whose campaign id is unchanged is KEPT; if its parent changed
 *            (because the parent was re-created or removed) it is moved with UpdateCampaignNode;
 *            anything else is CREATED under its parent (UpdateCampaignNode cannot change a
 *            campaign id, so an edited id is a new node and the old one is removed);
 *          then every loaded node that was not kept is DELETED, deepest first, since the
 *          service refuses to delete a node that still has children.
 *          The event's own node is created first if the event has none yet.
 *
 * Event nodes placed under this event (another event's LIFE_CYCLE_EVENT node) are handled apart
 * from campaigns, because the subtree beneath one is that other event's own hierarchy:
 *   - adding one creates a LIFE_CYCLE_EVENT node for that event under its parent. The picker only
 *     offers events that back no node yet (see eventIdsWithNodes): an event backs at most one node
 *     service-wide, and a node can never be made a root again, so moving an existing one in would
 *     be a one-way trip;
 *   - removing the event row itself deletes its node (the event is untouched, it just leaves this
 *     tree). The editor allows that only while no saved node sits beneath it — the service refuses
 *     to delete a node with children, and deleting them would wipe campaigns that may have been
 *     placed there from that other event. Unsaved rows beneath it simply go with it;
 *   - rows added beneath an event row are saved under its node like anywhere else. The hierarchy
 *     is one tree, so they also show in that event's own hierarchy;
 *   - if a campaign ABOVE one is removed, the event node is not deleted with it but moved up to
 *     this event's node, its own subtree travelling with it untouched.
 *
 * parent  {@link parentEventIdOf}: the event on the node directly above this event's node, when that
 *          node is an event (LIFE_CYCLE_EVENT). The service has no "parent event" field, so it is two
 *          reads: ListCampaignRoots?eventId= for this event's node (which carries `parentNodeId`), then
 *          GetCampaignNode on that parent. A parent that is an external campaign is not a parent
 *          event — the parent/child event relation is only event-under-event — so it yields none,
 *          rather than walking further up past the campaign.
 *
 * A failure stops the save at that call and is reported with what was being done. The caller
 * should reload: the calls already made have taken effect, and the next save must diff against
 * what the service now holds.
 *
 * Pure apart from the injected API, so it is tested without a network.
 */

import type { CampaignNode, CampaignTreeNode } from '../../client/EventCalendarClient';
import { newKey, pruneBlank, type HierarchyNode } from './campaignHierarchy';

/** The service calls a load or save needs. */
export interface HierarchyApi {
  findEventNode(eventId: string): Promise<{ nodeId?: string; parentNodeId?: string } | undefined>;
  getNode(nodeId: string): Promise<CampaignNode>;
  getTree(nodeId: string): Promise<CampaignTreeNode>;
  /** Every hierarchy's root node, all pages (`ListCampaignRoots` without `eventId`). */
  listRoots(): Promise<CampaignNode[]>;
  /** An event's node: a root when `parentNodeId` is absent, otherwise a child of it. */
  createEventNode(eventId: string, parentNodeId?: string): Promise<string>;
  createCampaignNode(sfdcCampaignId: string, parentNodeId: string): Promise<string>;
  reparent(nodeId: string, parentNodeId: string): Promise<void>;
  remove(nodeId: string): Promise<void>;
}

/** An event's hierarchy as the service holds it. */
export interface LoadedHierarchy {
  /** The event's own node, which the tree hangs from. Absent until the first save creates it. */
  anchorNodeId?: string;
  /** The nodes beneath it, as the editor holds them. */
  nodes: HierarchyNode[];
}

export const EMPTY_HIERARCHY: LoadedHierarchy = { nodes: [] };

/** The children of a service tree node, as editor nodes. */
export function nodesFromTree(tree: CampaignTreeNode): HierarchyNode[] {
  return (tree.children ?? []).map((child: CampaignTreeNode) => ({
    key: newKey(),
    nodeId: child.node?.nodeId,
    sfdcCampaignId: child.node?.sfdcCampaignId ?? '',
    eventId: child.node?.nodeType === 'LIFE_CYCLE_EVENT' ? child.node?.eventId : undefined,
    children: nodesFromTree(child),
  }));
}

/**
 * Every event that already backs a hierarchy node, in any tree — the events the picker must not
 * offer. One ListCampaignRoots, then one GetCampaignTree per root: there is no cheaper way with
 * today's API. Fine at a few hundred events a year; if the number of trees grows, this wants a
 * service-side field on the event instead.
 */
export async function eventIdsWithNodes(api: HierarchyApi): Promise<Set<string>> {
  const roots = await api.listRoots();
  const trees = await Promise.all(
    roots.filter((r) => r.nodeId).map((r) => api.getTree(r.nodeId!)),
  );
  const ids = new Set<string>();
  const walk = (tree: CampaignTreeNode) => {
    if (tree.node?.nodeType === 'LIFE_CYCLE_EVENT' && tree.node.eventId) ids.add(tree.node.eventId);
    (tree.children ?? []).forEach(walk);
  };
  trees.forEach(walk);
  // A root whose tree read came back without its own node still names its event.
  for (const r of roots) if (r.eventId) ids.add(r.eventId);
  return ids;
}

export async function loadHierarchy(api: HierarchyApi, eventId: string): Promise<LoadedHierarchy> {
  const anchor = await api.findEventNode(eventId);
  if (!anchor?.nodeId) return EMPTY_HIERARCHY;
  const tree = await api.getTree(anchor.nodeId);
  return { anchorNodeId: anchor.nodeId, nodes: nodesFromTree(tree) };
}

/**
 * The id of the event directly above `eventId` in its hierarchy, or undefined when there is none:
 * no node, a root node, or a parent node that is an external campaign (see the header).
 */
export async function parentEventIdOf(api: HierarchyApi, eventId: string): Promise<string | undefined> {
  const own = await api.findEventNode(eventId);
  if (!own?.parentNodeId) return undefined;
  const parent = await api.getNode(own.parentNodeId);
  return parent.nodeType === 'LIFE_CYCLE_EVENT' ? parent.eventId || undefined : undefined;
}

/** A failed save step, naming what was being done so the message can say which row. */
export class HierarchySaveError extends Error {
  constructor(step: string, cause: unknown) {
    super(`${step}: ${cause instanceof Error ? cause.message : String(cause)}`);
    this.name = 'HierarchySaveError';
  }
}

interface LoadedEntry {
  sfdcCampaignId: string;
  parentNodeId: string;
  depth: number;
  isEvent: boolean;
  node: HierarchyNode;
}

function indexLoaded(
  nodes: readonly HierarchyNode[],
  parentNodeId: string,
  depth: number,
  into: Map<string, LoadedEntry>,
) {
  for (const node of nodes) {
    if (!node.nodeId) continue;
    into.set(node.nodeId, {
      sfdcCampaignId: node.sfdcCampaignId.trim(),
      parentNodeId,
      depth,
      isEvent: !!node.eventId,
      node,
    });
    indexLoaded(node.children, node.nodeId, depth + 1, into);
  }
}

async function step<T>(label: string, call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (e: unknown) {
    throw new HierarchySaveError(label, e);
  }
}

/**
 * Makes the service hold `edited` beneath `eventId`'s node, given what was `loaded`. Returns the
 * saved hierarchy, every node carrying its nodeId, to become the next `loaded`.
 */
export async function saveHierarchy(
  api: HierarchyApi,
  eventId: string,
  loaded: LoadedHierarchy,
  edited: readonly HierarchyNode[],
): Promise<LoadedHierarchy> {
  const wanted = pruneBlank(edited);
  const before = new Map<string, LoadedEntry>();
  if (loaded.anchorNodeId) indexLoaded(loaded.nodes, loaded.anchorNodeId, 1, before);

  // Nothing there and nothing wanted: no event node to create for an empty tree.
  if (!loaded.anchorNodeId && wanted.length === 0) return EMPTY_HIERARCHY;

  const anchorNodeId =
    loaded.anchorNodeId ??
    (await step('Creating the event node', () => api.createEventNode(eventId)));

  const kept = new Set<string>();
  const place = async (
    nodes: readonly HierarchyNode[],
    parentNodeId: string,
  ): Promise<HierarchyNode[]> => {
    const out: HierarchyNode[] = [];
    for (const node of nodes) {
      const id = node.sfdcCampaignId.trim();
      const prior = node.nodeId ? before.get(node.nodeId) : undefined;
      let nodeId: string;
      if (prior && (prior.isEvent || prior.sfdcCampaignId === id)) {
        nodeId = node.nodeId!;
        kept.add(nodeId);
        if (prior.parentNodeId !== parentNodeId) {
          await step(`Moving ${id || 'an event node'}`, () => api.reparent(nodeId, parentNodeId));
        }
      } else if (node.eventId) {
        const eventId = node.eventId;
        nodeId = await step(`Adding event ${id || eventId}`, () =>
          api.createEventNode(eventId, parentNodeId),
        );
      } else {
        nodeId = await step(`Adding ${id}`, () => api.createCampaignNode(id, parentNodeId));
      }
      out.push({
        ...node,
        nodeId,
        sfdcCampaignId: id,
        children: await place(node.children, nodeId),
      });
    }
    return out;
  };
  const nodes = await place(wanted, anchorNodeId);

  /* An event node that is gone from the edited tree. Which of two things happened is read off its
     loaded parent: if that parent is still here (this event's node, or a kept campaign), the
     marketer removed the event row itself, so its node is deleted below. Otherwise it went
     because a campaign above it was removed, and it is moved up to this event's node instead,
     its own subtree travelling with it — so that subtree is kept, not deleted as gone. */
  const keepSubtree = (list: readonly HierarchyNode[]) => {
    for (const n of list) {
      if (n.nodeId) kept.add(n.nodeId);
      keepSubtree(n.children);
    }
  };
  const lifted = new Set<string>();
  for (const [nodeId, entry] of before) {
    if (!entry.isEvent || kept.has(nodeId)) continue;
    if (entry.parentNodeId === anchorNodeId || kept.has(entry.parentNodeId)) continue;
    lifted.add(nodeId);
  }
  for (const nodeId of lifted) {
    const entry = before.get(nodeId)!;
    // An event node under another lifted one travels with that one; only the topmost moves.
    let up = entry.parentNodeId;
    let underLifted = false;
    while (before.has(up)) {
      if (lifted.has(up)) underLifted = true;
      up = before.get(up)!.parentNodeId;
    }
    if (underLifted) continue;
    await step('Moving an event node up', () => api.reparent(nodeId, anchorNodeId));
    kept.add(nodeId);
    keepSubtree(entry.node.children);
    nodes.push(entry.node);
  }

  const removed = [...before.entries()]
    .filter(([nodeId]) => !kept.has(nodeId))
    .sort((a, b) => b[1].depth - a[1].depth);
  for (const [nodeId, entry] of removed) {
    const what = entry.isEvent ? `event ${entry.sfdcCampaignId || entry.node.eventId}` : entry.sfdcCampaignId;
    await step(`Removing ${what}`, () => api.remove(nodeId));
  }

  return { anchorNodeId, nodes };
}
